/**
 * NuovaPersonaDialog — creazione rapida di una scheda.
 *
 * Un solo campo obbligatorio (il nome) per una ragione precisa: la scheda si
 * crea SUBITO dopo la videochiamata, e tutto ciò che allunga quel momento la fa
 * non nascere. Il resto — tag, note, contatti — si aggiunge dal dettaglio, anche
 * a voce.
 */

import React, { useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, Stack, TextField, Typography,
} from '@mui/material';
import { tokens } from '../../theme';
import { oggiIso } from './labels';
import { TelefoniEditor } from './TelefoniEditor';
import { createPersona } from '../../services/reteContattiService';
import type { PersonaReteWriteInput } from '../../types/reteContatti';

interface Props {
  open: boolean;
  onClose: () => void;
  /** Creata: il chiamante ricarica e apre il dettaglio. */
  onCreated: (id: string) => void;
}

/** Il modulo vuoto: una riga di numero già pronta, perché di solito il numero c'è subito. */
const formVuoto = (): PersonaReteWriteInput => ({ nome: '', conosciuto_il: oggiIso(), telefoni: [{ numero: '' }] });

export const NuovaPersonaDialog: React.FC<Props> = ({ open, onClose, onCreated }) => {
  const [form, setForm] = useState<PersonaReteWriteInput>(formVuoto);
  const [salvando, setSalvando] = useState(false);
  const [errore, setErrore] = useState<string | null>(null);

  const set = <K extends keyof PersonaReteWriteInput>(k: K, v: PersonaReteWriteInput[K]) =>
    setForm((prev) => ({ ...prev, [k]: v }));

  const chiudi = () => {
    if (salvando) return;
    setForm(formVuoto());
    setErrore(null);
    onClose();
  };

  const salva = async () => {
    if (!form.nome.trim()) {
      setErrore('Il nome è obbligatorio.');
      return;
    }
    setSalvando(true);
    setErrore(null);
    try {
      const id = await createPersona(form);
      setForm(formVuoto());
      onCreated(id);
    } catch (e) {
      setErrore(e instanceof Error ? e.message : 'Creazione non riuscita');
    } finally {
      setSalvando(false);
    }
  };

  return (
    <Dialog open={open} onClose={chiudi} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontFamily: tokens.font.display, fontWeight: 600, pb: 0.5 }}>
        Nuova persona
        <Typography variant="body2" sx={{ color: tokens.color.ash, mt: 0.5, fontFamily: tokens.font.body }}>
          Basta il nome: il resto si aggiunge dopo, anche raccontandolo a voce.
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={2}>
          {errore && <Alert severity="error">{errore}</Alert>}

          <TextField
            label="Nome e cognome"
            value={form.nome}
            onChange={(e) => set('nome', e.target.value)}
            size="small"
            fullWidth
            autoFocus
            required
            onKeyDown={(e) => { if (e.key === 'Enter') salva(); }}
          />

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
            <TextField
              label="Azienda"
              value={form.azienda_testo ?? ''}
              onChange={(e) => set('azienda_testo', e.target.value)}
              size="small" fullWidth
            />
            <TextField
              label="Ruolo"
              value={form.ruolo_testo ?? ''}
              onChange={(e) => set('ruolo_testo', e.target.value)}
              size="small" fullWidth
            />
          </Stack>

          <TelefoniEditor telefoni={form.telefoni ?? []} onChange={(t) => set('telefoni', t)} />

          <TextField
            label="Email" type="email"
            value={form.email ?? ''}
            onChange={(e) => set('email', e.target.value)}
            size="small" fullWidth
          />

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
            <TextField
              label="Conosciuto il" type="date"
              value={form.conosciuto_il ?? ''}
              onChange={(e) => set('conosciuto_il', e.target.value)}
              size="small" InputLabelProps={{ shrink: true }} sx={{ minWidth: 170 }}
            />
            <TextField
              label="Dove/come"
              placeholder="videochiamata demo, fiera, presentato da…"
              value={form.conosciuto_come ?? ''}
              onChange={(e) => set('conosciuto_come', e.target.value)}
              size="small" fullWidth
            />
          </Stack>

          <Box>
            <Typography variant="caption" sx={{ color: tokens.color.mist }}>
              I contatti sono volutamente ridondanti: si perdono uno alla volta, non tutti insieme.
            </Typography>
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={chiudi} disabled={salvando} color="inherit">Annulla</Button>
        <Button
          onClick={salva}
          variant="contained"
          disabled={salvando || !form.nome.trim()}
          startIcon={salvando ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {salvando ? 'Creo…' : 'Crea scheda'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
